// Industry landing pages content — one entry per slug in INDUSTRIES.
// Each entry drives the shared IndustryTemplate. Wording follows the safe-copy
// policy: no invented client names, no invented accreditations, no claims to
// have worked in a specific named site, and no "we have X years in this
// sector" statements. Prefer "we support / we can help / typical requirements
// include".

export const INDUSTRY_PAGES = {
  // ---------------------------------------------------------------------------
  // Manufacturing & Production
  // ---------------------------------------------------------------------------
  manufacturing: {
    slug: "manufacturing",
    name: "Manufacturing & Production",
    heroImage: "/gallery/taping-cage.jpg",
    heroPosition: "center 40%",
    h1: "Engineering support for manufacturing and production sites.",
    subhead:
      "Mechanical, electrical and controls support for factories, machine shops and production lines — from a single new-machine install to rolling PPM across a whole facility.",
    seo: {
      title: "Engineering for Manufacturing & Production — North West",
      description:
        "Mechanical, electrical, controls and compliance support for manufacturing and production sites across the North West. Single-supplier engineering, planned or reactive.",
    },
    intro: `Manufacturing sites live and die on uptime. Every hour a line is down is an hour of lost throughput, lost margin and stressed operators — and every extra supplier involved in getting the line back up is another handoff, another conversation, another chance for the fix to arrive slower than it needs to. Lutrom supports manufacturing and production sites across the North West with mechanical, electrical and controls engineering under one accountable team, from our workshop in Northwich. We can help with new-machine installation and commissioning, refurbishment of critical assets, control-panel design and build, PLC programming and industrial automation projects, plus the routine compliance work — EICR, thermal imaging, PAT, planned preventive maintenance — that keeps insurers, auditors and head office happy without pulling engineering time away from production. Whether you run a single-site machine shop, a multi-line high-volume factory, or a lower-volume, higher-mix production environment, the mix of services will look different but the underlying pattern is the same: we scope the work honestly, we quote fixed prices where the scope is fixed, and we hand over documentation packs that make future work easier — not harder — for whoever picks it up.`,
    typicalRequirements: [
      "New-machine installation and commissioning — off-load, position, wire, commission, sign off",
      "Machine refurbishment — extend the life of critical production assets",
      "Control-panel design, build and installation to BS EN 61439",
      "PLC programming and HMI development — new builds, upgrades and fault-finding",
      "Reactive breakdown response for line-down electrical, mechanical or controls emergencies",
      "Planned preventive maintenance — mechanical, electrical and controls under one contract",
      "EICR / fixed-wire testing and PAT testing for compliance",
      "Thermal imaging and condition monitoring on switchgear, motors and drives",
    ],
    howWeSupport: [
      {
        title: "Single-supplier engineering",
        body: "Mechanical, electrical, controls and compliance under one team removes the cross-trade coordination overhead. Fewer handoffs, one accountable point when something needs to be revisited.",
      },
      {
        title: "Planned or reactive",
        body: "You can engage us as a rolling PPM contract, on a project-by-project basis for capex and installations, or purely for reactive callout cover — or any mix of the three.",
      },
      {
        title: "Documented handover",
        body: "Every piece of work we do lands with a documentation pack — schematics, test records, PLC code archive, safety validation — so your engineering team is never guessing about what changed.",
      },
      {
        title: "Fixed-price where possible",
        body: "Where the scope is defined we quote fixed. Where it isn't, we quote a survey first and a fixed price against the survey findings. Day-rate ad-hoc work is the exception, not the default.",
      },
    ],
    servicesFocus: [
      "machine-installation-and-commissioning",
      "machine-refurbishment",
      "control-panel-design-and-build",
      "plc-programming",
      "industrial-automation",
      "planned-preventive-maintenance",
    ],
    responsePromise:
      "Our team operates from our Northwich workshop and covers the North West. Response performance depends on distance and current engineer availability — we give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "Can you support us as our primary engineering partner rather than as a specialist called in ad-hoc?",
        a: "Yes — that's the arrangement we tend to prefer, because it means we build up real knowledge of your site rather than resetting on every visit. It can start as a rolling PPM contract that expands into project and capex work over time, or as a full outsourced engineering relationship from day one.",
      },
      {
        q: "Do you work around production windows?",
        a: "Yes. Most of what we do on manufacturing sites happens alongside live production, with disruptive work scheduled into planned shutdowns or out-of-hours windows. Isolation strategy, permit-to-work and any out-of-hours premium are agreed and priced before we start.",
      },
      {
        q: "What if we need something bespoke that isn't on your services list?",
        a: "Please ask. The list is a starting point, not a wall. Where a project sits inside our mechanical / electrical / controls capabilities, we're happy to scope it. Where it doesn't, we'll say so plainly rather than take on something we can't deliver well.",
      },
      {
        q: "How do you handle safety and compliance on our site?",
        a: "We work to your site's permit-to-work and safety documentation regime by default, with our own RAMS produced and shared before we start. Where you don't have an existing regime, we bring ours. On regulated work (fire alarms, EICR, thermal imaging) certificates are issued digitally, same-day where possible.",
      },
      {
        q: "Do you have experience with our specific PLC / control system?",
        a: "We work across the mainstream PLC and control platforms. Tell us what's installed and we'll confirm fit before quoting; we don't take on work on platforms we can't support well. Where a platform is genuinely obsolete or specialist we'll say so and — where it makes sense — recommend a modernisation route.",
      },
      {
        q: "Can you take on multi-site accounts?",
        a: "Yes. Multi-site accounts are consolidated into a single register with per-site schedules, one invoice and portfolio-level reporting so head office can see compliance status across every site without chasing individual certificates.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Food & Beverage
  // ---------------------------------------------------------------------------
  "food-and-beverage": {
    slug: "food-and-beverage",
    name: "Food & Beverage",
    heroImage: "/gallery/extra-08.jpg",
    heroPosition: "center 40%",
    h1: "Engineering support for food and beverage sites.",
    subhead:
      "Washdown-ready electrical, hygienic mechanical work and audit-ready compliance across bakery, dairy, meat, drinks, ready-meal and packaging environments.",
    seo: {
      title: "Engineering for Food & Beverage — Hygienic Industrial North West",
      description:
        "Washdown-ready electrical, hygienic mechanical and audit-ready compliance support for food and beverage sites across the North West. Single-supplier engineering.",
    },
    intro: `Food and beverage sites operate under a stricter set of rules than most industrial environments — hygienic radii, cleanable finishes, IP-rated washdown-ready electrical, contamination-controlled work practices, and an audit trail that can withstand a BRC, SALSA or customer audit at short notice. Lutrom supports F&B clients across the North West with mechanical, electrical, controls and compliance work sized for that environment: stainless welded pipework and hygienic radii on the mechanical side, IP-rated enclosures and washdown-ready installs on the electrical side, PLC and HMI work that respects the batch-record and traceability requirements common on regulated production, and PPM contracts that book work into your production calendar rather than fighting it. Because hygiene, safety and audit-readiness are typical requirements, we build them into the quote and the scope from the start — RAMS aligned to your food-safety regime, hygienic detail called out on drawings, traceable materials for critical work, and documentation packs handed over in a format that slots into your existing quality-management system.`,
    typicalRequirements: [
      "IP-rated, washdown-ready electrical installations and enclosures",
      "Hygienic stainless pipework, hygienic radii and cleanable finishes",
      "Machine installation, refurbishment and controls upgrades on line equipment",
      "Bespoke machinery — infeed / outfeed conveyors, dosing and secondary packaging",
      "PLC / HMI development with batch-record and traceability requirements in mind",
      "Compressed air, filtration and hygienic water services",
      "Planned preventive maintenance built around your production calendar",
      "EICR, PAT, thermal imaging and fire-alarm compliance to keep audits smooth",
    ],
    howWeSupport: [
      {
        title: "Built around audit",
        body: "Documentation packs, method statements and material traceability are produced in formats that slot straight into your existing quality-management system — reducing audit prep time.",
      },
      {
        title: "Production-calendar aware",
        body: "Planned work is scheduled around production runs, cleaning cycles and shutdown windows. Where downtime is unavoidable, we quote out-of-hours or shift-adjacent working from the start.",
      },
      {
        title: "Hygienic detail as default",
        body: "Hygienic radii, cleanable finishes and IP-rated washdown-ready electrical are built into the design and scope by default — not treated as extras when the auditor asks the question.",
      },
      {
        title: "Multi-trade under one team",
        body: "Mechanical, electrical, controls and compliance under one accountable point means less cross-supplier coordination and fewer handoffs where interfaces cause missed detail.",
      },
    ],
    servicesFocus: [
      "machine-installation-and-commissioning",
      "welding-and-fabrication",
      "water-systems-and-pipework",
      "hydraulics-and-pneumatics",
      "control-panel-design-and-build",
      "planned-preventive-maintenance",
    ],
    responsePromise:
      "Our engineers work from our Northwich base and cover food and beverage sites across the North West. Where your site has specific hygiene, GMP or audit requirements, tell us at the point of enquiry and we'll confirm fit before we quote.",
    faq: [
      {
        q: "Can you work to our site hygiene and food-safety documentation?",
        a: "Yes. We produce our own RAMS and align them to your site's hygiene, food-safety and audit documentation before we start work. Where your site has specific glass, plastic or metal control policies we work within those and note the controls we've applied in the handover pack.",
      },
      {
        q: "Do you do stainless-food-grade welding?",
        a: "Yes, in 304 and 316L stainless — TIG-welded with polished welds and hygienic radii where required. For hygiene-critical or pressure-vessel work we can weld against a written procedure and document welder qualification.",
      },
      {
        q: "What about IP-rated washdown-ready electrical installs?",
        a: "Enclosures, containment, cable glanding and terminations are specified to the IP rating the environment actually requires — not a rule of thumb. IP66 washdown-rated is common in wet-process areas; IP54 or higher in packaging halls. Everything documented on the schematic and the handover pack.",
      },
      {
        q: "Can you support BRC / SALSA / customer-audit prep?",
        a: "Where the engineering documentation is part of the audit scope, we hand over our records in a format that slots into your existing QMS. We can't sign off compliance items outside our own scope, but we can and do make sure our own work is audit-ready.",
      },
      {
        q: "Do you handle the controls and traceability layers?",
        a: "Yes. HMI, SCADA, batch-record capture and traceability integrations are inside our scope. For regulated environments we design HMI/SCADA with role-based access, audit trails and where required electronic-signature workflows aligned to your compliance framework.",
      },
      {
        q: "Can you cover breakdowns during a production run?",
        a: "Yes — where agreed as part of your PPM package or on a per-callout basis. Response performance depends on distance from Northwich and current engineer availability; we give an honest ETA on the phone when the callout is logged so you can plan around it.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Pharmaceutical
  // ---------------------------------------------------------------------------
  pharmaceutical: {
    slug: "pharmaceutical",
    name: "Pharmaceutical & Life Sciences",
    heroImage: "/gallery/extra-09.jpg",
    heroPosition: "center 40%",
    h1: "Engineering support for pharmaceutical and life sciences sites.",
    subhead:
      "GMP-aware mechanical, electrical and controls work for regulated environments, clean utilities and validated equipment — with documentation that stands up to inspection.",
    seo: {
      title: "Engineering for Pharmaceutical & Life Sciences — North West",
      description:
        "GMP-aware mechanical, electrical, controls and utilities engineering for pharmaceutical and life sciences sites across the North West. Validation-ready documentation.",
    },
    intro: `Pharmaceutical and life-sciences sites are the most documentation-intensive industrial environments most engineers ever encounter. Every change, every calibration, every material change and every controls modification produces a paper trail that has to withstand regulatory inspection. Lutrom supports pharma and life-sciences clients across the North West with mechanical, electrical, controls and utilities engineering delivered with that documentation load in mind. We can help with mechanical work on clean utilities and process pipework, electrical installations designed for the containment and cleanroom requirements typical of the sector, control-system work on PLC, HMI and SCADA with the audit-trail and role-based access controls that regulated production requires, and compliance activities aligned to your existing quality-management system. Where your site operates under a specific quality framework (GMP, 21 CFR Part 11 / Annex 11, ISO 14644 for cleanrooms), tell us at the point of enquiry — we can confirm fit before we quote, produce our documentation in the format your QA team needs, and align our scope with your change-control regime rather than fighting it.`,
    typicalRequirements: [
      "Clean-utility mechanical work — purified water systems, WFI distribution, clean steam",
      "Process pipework in stainless steel — orbital or hand-TIG, hygienic detail throughout",
      "Electrical installations designed for containment / cleanroom / classified areas",
      "PLC / HMI / SCADA with role-based access, audit trails and electronic signatures",
      "Alarm-management and rationalisation aligned to ISA-18.2 principles",
      "Change-control-aware engineering — documentation aligned to your QMS",
      "Calibration-friendly control loops and instrumentation",
      "Compliance activities — EICR, thermal imaging, PPM — scheduled around GMP shutdowns",
    ],
    howWeSupport: [
      {
        title: "Documentation that clears QA",
        body: "Handover packs produced in formats designed to slot into your QMS — validation-ready where the scope requires it, aligned to your existing change-control regime.",
      },
      {
        title: "Regulated-environment controls",
        body: "Where 21 CFR Part 11 or Annex 11 requirements apply, we can work to your compliance framework and documentation requirements — with our engineering documentation produced in the format your QA and validation teams need.",
      },
      {
        title: "Change-control aware",
        body: "Every piece of work is scoped with change-control in mind. We produce impact statements, RAMS and detailed handover records so your QA team isn't rebuilding the paper trail after we've left.",
      },
      {
        title: "Multi-trade under one accountable team",
        body: "Mechanical, electrical, controls and compliance under one point of accountability — critical when a single validated system spans multiple engineering disciplines.",
      },
    ],
    servicesFocus: [
      "water-systems-and-pipework",
      "hmi-and-scada-development",
      "industrial-automation",
      "control-panel-design-and-build",
      "welding-and-fabrication",
      "planned-preventive-maintenance",
    ],
    responsePromise:
      "Our engineers work from our Northwich base and cover pharma and life-sciences sites across the North West. Where your site has specific GMP, cleanroom or validation requirements, tell us at the point of enquiry so we can confirm fit before quoting.",
    faq: [
      {
        q: "Can you work under our change-control regime?",
        a: "Yes. Change-control is the default assumption on regulated work rather than an exception. We produce impact statements, RAMS and detailed handover records aligned to your regime, and we won't start work until the change has been approved by your QA / engineering team.",
      },
      {
        q: "Do you produce validation documentation?",
        a: "Where the scope requires validation-ready documentation (IQ / OQ / PQ protocols and reports), we produce them to the format your validation team specifies. We can't sign off validation on your behalf — that's a QA-owned activity — but we produce the engineering documentation to support it.",
      },
      {
        q: "What about 21 CFR Part 11 / Annex 11 compliance in the controls layer?",
        a: "Where 21 CFR Part 11 or Annex 11 requirements apply, we can work to your compliance framework and documentation requirements. We hand over engineering documentation in a format that supports your CSV (Computer Systems Validation) activity, and align our scope with the controls your compliance framework requires.",
      },
      {
        q: "Can you support us on clean-utility work?",
        a: "Yes — purified water systems, WFI distribution, clean steam and compressed-air / gas systems are inside our scope. Mechanical is executed in stainless (typically 316L) with the welding standard and finish appropriate to the utility.",
      },
      {
        q: "Do you handle work on validated equipment?",
        a: "Yes, subject to your change-control regime. Modifications to validated equipment need proper documentation and QA sign-off before and after; we work within that regime and produce the engineering documentation your QA team needs to close the change.",
      },
      {
        q: "How do you handle contamination control on active production sites?",
        a: "We work to your site's contamination-control regime by default — gowning, tooling controls, material controls, exit inspection. Where the site has specific policies (metal control, glass control, foreign-body control), we work within those and note the controls we applied in the handover pack.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Logistics & Warehousing
  // ---------------------------------------------------------------------------
  "logistics-and-warehousing": {
    slug: "logistics-and-warehousing",
    name: "Logistics & Warehousing",
    heroImage: "/gallery/extra-06.jpg",
    heroPosition: "center 40%",
    h1: "Engineering support for logistics and warehousing sites.",
    subhead:
      "Conveyor systems, sortation, dock-loading equipment, electrical distribution and rapid-response support for distribution centres and warehouse operations.",
    seo: {
      title: "Engineering for Logistics & Warehousing — DC & Sortation Support",
      description:
        "Conveyor, sortation, electrical distribution and reactive support for logistics and warehousing sites across the North West. Multi-trade engineering under one team.",
    },
    intro: `A distribution centre or logistics site behaves like a manufacturing plant on caffeine — the same rhythms of uptime, breakdown, planned maintenance and capex, but at a faster clock speed and with less tolerance for downtime because the trucks arriving on the loading bay don't wait. Lutrom supports logistics and warehousing clients across the North West with mechanical, electrical and controls engineering aligned to that reality: conveyor and sortation systems that need to keep moving, dock-loading equipment that has to be safe and available, warehouse-management-system integration where the control layer has drifted from its documentation, LED lighting and small-power distribution across large sheds, and PPM contracts that fit around peak-season constraints (nothing disruptive during the Christmas run). We can help with new-build fit-outs and expansions, ad-hoc modifications and repairs, and rolling PPM designed to prevent the breakdowns that hurt most — sortation and infeed lines during peak, dock levellers during trailer changeovers, MHE charging bays in the early hours.`,
    typicalRequirements: [
      "Conveyor and sortation systems — install, modify, fault-find, upgrade",
      "Dock-loading equipment — levellers, seals, doors, safety-interlock circuits",
      "Warehouse electrical distribution — LED lighting, small power, charge bays",
      "Controls integration — PLC, HMI, and WMS interface where the layer needs work",
      "Emergency-lighting and fire-alarm compliance across large-footprint sheds",
      "Thermal imaging on switchgear, motor drives and charge bays to catch faults early",
      "Peak-season-aware PPM contracts — planned work outside peak windows",
      "Rapid-response breakdown support for sortation and infeed lines",
    ],
    howWeSupport: [
      {
        title: "Peak-season aware",
        body: "PPM schedules and disruptive works are planned around your peak windows — we know Christmas, Black Friday and end-of-quarter are not the moments to be swapping motors on the main sortation line.",
      },
      {
        title: "Fast-response for the assets that matter",
        body: "Breakdown priority is agreed up-front by asset — sortation, infeed / outfeed, dock levellers and charge bays get first attention where the contract calls for it.",
      },
      {
        title: "Multi-trade shed cover",
        body: "One team covers mechanical, electrical and controls across the whole footprint — no need to call separate suppliers for the dock leveller, the conveyor and the switchgear.",
      },
      {
        title: "Documented assets",
        body: "Every asset we work on is added to a documented register with photographs, drawings and status — so future work isn't a scavenger hunt through legacy paperwork.",
      },
    ],
    servicesFocus: [
      "24-7-emergency-breakdown-callout",
      "planned-preventive-maintenance",
      "industrial-electrical-installation",
      "control-panel-design-and-build",
      "plc-programming",
      "thermal-imaging-and-condition-monitoring",
    ],
    responsePromise:
      "Our engineers work from our Northwich base and cover logistics and warehousing sites across the North West. For time-critical assets, response priority is agreed as part of your PPM scope — we'll give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "Can you cover us during peak season?",
        a: "Yes — peak-season cover is exactly what most of our logistics clients want most. Peak-priority response and out-of-peak-hours planned work are agreed at contract level so the arrangement is built for the moments it matters most.",
      },
      {
        q: "Do you handle conveyor and sortation work?",
        a: "Yes — install, modify, fault-find and upgrade across the range. Both the mechanical (rollers, belts, drives, alignment) and electrical / controls (motor circuits, PLC, safety interlocks) sides are inside our scope, which removes the split-supplier problem that plagues most conveyor breakdowns.",
      },
      {
        q: "What about dock-loading equipment?",
        a: "Levellers, seals, doors and safety-interlock circuits — install, service and fault-find. Common on our PPM schedules for distribution centres, both because they're safety-critical and because they see a lot of cycles.",
      },
      {
        q: "Do you integrate with WMS / warehouse-management systems?",
        a: "Where the integration is at the PLC/SCADA layer (barcode scans, weight capture, sortation destination messages back to the WMS), yes. We work to the WMS vendor's interface spec and validate against a test environment before going live. Direct WMS software work sits with the WMS vendor.",
      },
      {
        q: "Can you take on new-build and expansion works?",
        a: "Yes — from a single new dock leveller install through to full electrical and controls fit-out of a new mezzanine or extension. Fit-outs are priced fixed against a written design and delivered to a milestone plan.",
      },
      {
        q: "Do you work with MHE / battery-charge equipment?",
        a: "Yes — charging-bay electrical distribution, small-power and safety-interlock circuits are inside our scope. Where the equipment is under an MHE OEM's own service agreement, we co-ordinate with them rather than cut across.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Retail & Commercial
  // ---------------------------------------------------------------------------
  "retail-and-commercial": {
    slug: "retail-and-commercial",
    name: "Retail & Commercial",
    heroImage: "/gallery/extra-05.jpg",
    heroPosition: "center 40%",
    h1: "Engineering support for retail and commercial estates.",
    subhead:
      "Multi-site rolling maintenance, one-off installs and compliance testing for retail chains, commercial estates and office portfolios across the North West.",
    seo: {
      title: "Engineering for Retail & Commercial Estates — Multi-Site",
      description:
        "Rolling maintenance, EICR / PAT compliance and installation works for retail chains and commercial estates across the North West. Multi-site single-account service.",
    },
    intro: `Retail and commercial estates run on a different maintenance rhythm to industrial sites — smaller assets, more of them, spread across more locations, with hard constraints around trading hours, customer footfall and site access. Lutrom supports retail chains, multi-site portfolios and commercial estates across the North West with the mix of routine compliance, reactive callouts and one-off installation works that this kind of estate needs. We can help with EICR / fixed-wire testing rolled out across a portfolio on a scheduled basis, PAT testing with consolidated digital asset registers, fire-alarm and emergency-lighting servicing to statutory intervals, LED and lighting-control refits, and one-off electrical works for store refurbishments, brand rollouts or fit-outs. Multi-site accounts are consolidated into a single register with per-site schedules, one invoice, and portfolio-level reporting so head office can see compliance status across the whole estate without chasing individual certificates from a dozen local contractors.`,
    typicalRequirements: [
      "Multi-site EICR / fixed-wire testing on a rolling schedule",
      "PAT testing across whole portfolios with consolidated digital asset registers",
      "Fire-alarm servicing to BS 5839 and emergency lighting to BS 5266",
      "LED lighting refits, lighting-control installs and small-power upgrades",
      "Store fit-out and refurbishment electrical works — out-of-hours where required",
      "Reactive callout cover for retail and commercial estate assets",
      "Thermal imaging on distribution boards for insurer-driven surveys",
      "Single-account portfolio management with consolidated reporting",
    ],
    howWeSupport: [
      {
        title: "One account, one view",
        body: "Multi-site portfolios are consolidated into a single account with per-site schedules, one invoice and portfolio-level reporting — so head office sees the whole estate rather than chasing individual certificates.",
      },
      {
        title: "Out-of-hours friendly",
        body: "Retail and commercial estates don't shut for maintenance — most work happens outside trading hours, and out-of-hours premium is priced up-front rather than as a surprise on the invoice.",
      },
      {
        title: "Statutory-cycle scheduling",
        body: "EICR, PAT, fire-alarm and emergency-lighting testing all follow statutory intervals; we schedule them into a single calendar view so nothing gets missed and nothing is done twice.",
      },
      {
        title: "One trade set, all sites",
        body: "Electrical, testing and compliance work across the whole estate delivered by one team rather than a patchwork of local suppliers. Consistent standard, one accountable point.",
      },
    ],
    servicesFocus: [
      "eicr-and-fixed-wire-testing",
      "pat-testing",
      "fire-alarms-and-emergency-lighting",
      "industrial-electrical-installation",
      "thermal-imaging-and-condition-monitoring",
      "planned-preventive-maintenance",
    ],
    responsePromise:
      "Our engineers work from our Northwich base and cover retail and commercial estates across the North West. Where trading hours or site-access windows constrain when we can attend, we schedule and price accordingly at quote stage.",
    faq: [
      {
        q: "Can you take over an existing multi-site compliance programme?",
        a: "Yes — this is very common. We take on the existing asset register (whatever format it's in), verify against site reality on the first cycle, and rebuild it as our digital register. Existing certificates stay valid until their scheduled retest; from that point the estate is on our schedule.",
      },
      {
        q: "How do you handle out-of-hours access?",
        a: "Out-of-hours attendance is booked into the schedule with the out-of-hours premium priced up-front. For retail estates the standard is post-trading; for offices it's usually evenings and weekends. Access arrangements (keyholders, alarm codes, security induction) are agreed before the first visit.",
      },
      {
        q: "Can you handle brand rollouts and fit-outs?",
        a: "Yes — store fit-outs, refurbishments and brand rollouts sit inside our electrical installation scope. Where the rollout is across multiple sites we can align the schedule with your project timeline and hand over consistent documentation from every site.",
      },
      {
        q: "Do you provide consolidated reporting for head office?",
        a: "Yes. Portfolio dashboards show compliance status across every site with drill-down to individual certificates. Automated reminders flag upcoming statutory work before it's due. Head office access is separate from site access so the right people see the right level of detail.",
      },
      {
        q: "What about facilities-management integration?",
        a: "Where you already have an FM provider running the estate, we're happy to work under their contract for the technical work they don't cover directly. Where you'd rather deal with the electrical / testing / compliance layer directly, we can operate as a specialist supplier alongside your FM.",
      },
      {
        q: "How is invoicing handled on multi-site accounts?",
        a: "Single monthly invoice with per-site line breakdown by default, or per-site invoicing where head office wants each site to bear its own cost. Whichever pattern works with your finance team — we'll set the account up either way.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Education & Schools
  // ---------------------------------------------------------------------------
  "education-and-schools": {
    slug: "education-and-schools",
    name: "Education & Schools",
    heroImage: "/gallery/extra-04.jpg",
    heroPosition: "center 40%",
    h1: "Engineering support for schools, colleges and universities.",
    subhead:
      "EICR, fire-alarm servicing, PAT and electrical works for education sites — planned around term dates so disruption stays outside the classroom.",
    seo: {
      title: "Engineering for Schools, Colleges & Universities — Compliance & Works",
      description:
        "EICR, fire-alarm servicing, PAT testing and electrical works for education sites across the North West. Work scheduled around term dates and safeguarding requirements.",
    },
    intro: `Education sites operate on a calendar that doesn't bend — term dates are fixed, exams are non-negotiable, and any disruptive engineering work has to happen outside those windows or not at all. Lutrom supports schools, colleges and universities across the North West with the mix of statutory compliance and installation works that education estates typically need: EICR / fixed-wire testing rolled out room-by-room or block-by-block around term dates, PAT testing scheduled into holiday windows for the stock rebuild, fire-alarm and emergency-lighting servicing to statutory intervals with reports formatted for the responsible person, LED refurbishments and lighting upgrades typically funded through phased capital programmes, and one-off electrical installation works for new IT rooms, science labs, kitchens and site expansions. We can discuss DBS and safeguarding requirements at the point of enquiry, and access is booked in advance through your site management rather than turning up unannounced.`,
    typicalRequirements: [
      "EICR / fixed-wire testing scheduled around term dates and exam periods",
      "PAT testing during holiday windows — typically the biggest single visit of the year",
      "Fire-alarm servicing to BS 5839 and emergency lighting to BS 5266",
      "LED lighting refurbishments funded through phased capital programmes",
      "Electrical installations for new IT rooms, science labs, kitchens and expansions",
      "Small power, distribution upgrades and DB replacements between terms",
      "Thermal imaging for insurer-driven or CIF (Condition Improvement Fund) surveys",
      "Reactive callout support during term time for safety-critical faults",
    ],
    howWeSupport: [
      {
        title: "Term-date aware scheduling",
        body: "All planned work is booked around your term calendar and exam periods. Disruptive elements are scheduled into holidays; term-time visits are limited to non-disruptive activity or safety-critical reactive callouts.",
      },
      {
        title: "Safeguarding-aware",
        body: "We can discuss DBS and safeguarding requirements at the point of enquiry. Visits are booked in advance through your site management rather than showing up unannounced.",
      },
      {
        title: "Reports for the responsible person",
        body: "Certificates and reports are formatted for the site's responsible person (business manager, estates lead, headteacher) rather than for an engineering audience — clear, actionable and audit-ready.",
      },
      {
        title: "Multi-site portfolio ready",
        body: "For MATs (Multi-Academy Trusts) and university estates, work is consolidated into a portfolio view with per-site schedules and centralised reporting for the trust or estates director.",
      },
    ],
    servicesFocus: [
      "eicr-and-fixed-wire-testing",
      "pat-testing",
      "fire-alarms-and-emergency-lighting",
      "industrial-electrical-installation",
      "planned-preventive-maintenance",
      "thermal-imaging-and-condition-monitoring",
    ],
    responsePromise:
      "Our engineers work from our Northwich base and cover education sites across the North West. Reactive callouts during term time are prioritised for safety-critical faults; non-critical work is booked into the next holiday window unless agreed otherwise.",
    faq: [
      {
        q: "Do you handle DBS and safeguarding requirements?",
        a: "We can discuss DBS and safeguarding requirements at the point of enquiry. Please tell us what your site expects from visiting contractors and we'll confirm fit before we attend.",
      },
      {
        q: "Can you fit around our term calendar?",
        a: "Yes — term-date scheduling is the default assumption on our education work. The annual calendar is agreed at contract level, with big planned visits (PAT, EICR, fire-alarm annuals, LED refits) all booked into holiday windows.",
      },
      {
        q: "Do you work with MATs and multi-site trusts?",
        a: "Yes. MAT and university estate work is consolidated into a portfolio view with per-site schedules, single invoicing and centralised reporting for the trust or estates director. Individual schools retain their own visit schedules; head office sees the whole picture.",
      },
      {
        q: "Can you support LED refurbishments funded through CIF or similar?",
        a: "Yes — LED refurbishments funded through Condition Improvement Fund or similar capital programmes are common on our schedule. We can survey, produce a fixed-price scope, and deliver the works to the funding-body timeline (usually holiday windows).",
      },
      {
        q: "What about reactive callouts during term time?",
        a: "Prioritised for safety-critical faults — loss of power to occupied areas, fire-alarm faults, emergency-lighting failures, safeguarding-relevant issues. Non-critical faults are logged and booked into the next holiday window unless agreed otherwise.",
      },
      {
        q: "Do you handle science-lab or kitchen electrical works?",
        a: "Yes — new lab installs, kitchen refurbishments and specialist small-power (gas-interlock circuits, fume-cupboard controls, wash-up sinks) are inside our electrical scope. Coordination with the lab or kitchen fit-out contractor is included in the project management.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Sport & Leisure
  // ---------------------------------------------------------------------------
  "sport-and-leisure": {
    slug: "sport-and-leisure",
    name: "Sport & Leisure",
    heroImage: "/gallery/extra-07.jpg",
    heroPosition: "center 40%",
    h1: "Engineering support for sport, stadium and leisure sites.",
    subhead:
      "Stadium electrical, floodlighting, back-of-house engineering and compliance for sport, leisure and events venues across the North West.",
    seo: {
      title: "Engineering for Sport, Stadiums & Leisure Venues — North West",
      description:
        "Stadium electrical, LED floodlighting, back-of-house engineering and compliance for sport and leisure venues across the North West. Fixture-calendar-aware scheduling.",
    },
    intro: `Sport, stadium and leisure venues operate on a fixture calendar rather than a production calendar — big events, big crowds, and long stretches between where the venue is empty and available for engineering work. Lutrom supports sport, leisure and events sites across the North West with the mix of high-impact electrical works and routine compliance that this kind of venue typically needs: stadium and floodlighting electrical installations, LED sports-lighting refurbishments where funding or sustainability targets drive the upgrade, back-of-house electrical for hospitality, media and broadcast facilities, dressing-room and training-facility installs, compliance work (EICR, PAT, fire-alarm servicing) planned around the fixture calendar rather than fighting it, and reactive-callout cover for match-day or event-critical failures. We can work alongside your venue team, contractors and responsible persons where safety, access control and event schedules affect engineering work — producing our documentation in a format that fits your existing operational routine.`,
    typicalRequirements: [
      "Stadium electrical installations — main incoming, sub-mains, hospitality distribution",
      "LED sports-lighting refurbishments and floodlighting installs",
      "Back-of-house electrical for media, broadcast and hospitality areas",
      "Dressing-room, training-facility and gym electrical installations",
      "Fire-alarm servicing, emergency-lighting compliance and PAT testing",
      "EICR / fixed-wire testing scheduled around the fixture calendar",
      "Reactive-callout cover for match-day or event-critical faults",
      "Thermal imaging on venue switchgear to support insurer-driven surveys",
    ],
    howWeSupport: [
      {
        title: "Fixture-calendar scheduling",
        body: "Planned works are scheduled around the fixture calendar and off-season windows. Where a fixture-critical fault emerges, reactive-callout priority is agreed at contract level so match-day never becomes an engineering crisis.",
      },
      {
        title: "Works alongside your venue team",
        body: "We can work alongside your venue team, contractors and responsible persons where safety, access control and event schedules affect engineering work — producing our documentation in a format that fits your existing operational routine.",
      },
      {
        title: "Broadcast-friendly engineering",
        body: "Back-of-house work in media and broadcast areas is scoped with EMC and signal-integrity requirements in mind — power quality, cable routing and containment separation done properly the first time.",
      },
      {
        title: "Multi-trade under one team",
        body: "Electrical, controls, fire-alarm and compliance work delivered by one accountable team — critical for match-day-critical assets where a single supplier point makes the reactive path much faster.",
      },
    ],
    servicesFocus: [
      "industrial-electrical-installation",
      "fire-alarms-and-emergency-lighting",
      "eicr-and-fixed-wire-testing",
      "pat-testing",
      "control-panel-design-and-build",
      "planned-preventive-maintenance",
    ],
    responsePromise:
      "Our engineers work from our Northwich base and cover sport, stadium and leisure venues across the North West. Match-day / event-day reactive priority is agreed as part of your PPM or callout scope — we'll give an honest ETA on the phone when the callout is logged.",
    faq: [
      {
        q: "Can you support match-day or event-day reactive callouts?",
        a: "Yes — match-day / event-day reactive priority is agreed at contract level with a specified response commitment. Between events we schedule planned works to fit around the fixture calendar so nothing disruptive lands on an event day.",
      },
      {
        q: "Do you do LED sports-lighting refurbishments?",
        a: "Yes — LED floodlighting and sports-lighting refurbishments are common on our schedule, typically funded through capital programmes or sustainability-linked funding. We can survey, produce a fixed-price scope and deliver to the funding body's timeline.",
      },
      {
        q: "What about back-of-house and broadcast electrical?",
        a: "Yes — media, broadcast and hospitality electrical works are inside our scope. Broadcast areas are scoped with EMC and signal-integrity in mind (power quality, cable-routing separation, dedicated distribution where required).",
      },
      {
        q: "How is compliance work handled at a working venue?",
        a: "EICR, PAT, fire-alarm and emergency-lighting testing are scheduled to slot into your fixture calendar and off-season windows. Certificates are produced in a format that fits your existing operational and compliance documentation without additional reformatting.",
      },
      {
        q: "Can you take on new-build or expansion works at a venue?",
        a: "Yes — from a single new hospitality suite install through to major expansion works, electrical and controls scope is inside our capability. Fit-outs are priced fixed against a written design and delivered to a milestone plan.",
      },
      {
        q: "Do you work with sub-tenant / concession operators on site?",
        a: "Yes — where the head venue has sub-tenant caterers, retail concessions or gym operators, we're happy to work across those tenants under a single account, with per-tenant billing where the estate operator wants each concession to bear its own cost.",
      },
    ],
  },
};
